import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import '../components/auth/Auth.css';
import './Pages.css';
import logo from './image/tidealogo.jpg';

const ClaimAccount = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [token, setToken] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  const { claimGuestAccount, loading, error } = useAuth();
  
  // Extract token from URL on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const guestToken = queryParams.get('token');
    
    if (!guestToken) {
      // No action needed here as we'll show an error message in the UI
    } else {
      setToken(guestToken);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const { password, confirmPassword } = formData;
    
    // Form validation
    if (!password || !confirmPassword) {
      return; // HTML5 validation will handle this
    }
    
    if (password !== confirmPassword) {
      // We could handle this with state, but for simplicity
      // we'll rely on the context's error handling
      return;
    }
    
    if (password.length < 6) {
      // Again, relying on context's error handling
      return;
    }
    
    try {
      const response = await claimGuestAccount(token, password);
      
      setSuccess(true);
      setEmail(response.email);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            accountClaimed: true,
            email: response.email 
          } 
        });
      }, 2000);
      
    } catch (err) {
      // Error is handled by the context
      console.error('Account claim error:', err);
    }
  };

  // Show error if no token is provided
  const tokenError = !token && !loading ? (
    <div className="error-message">
      {t('invalid_or_missing_token')}
    </div>
  ) : null;

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="logo-container">
          <img src={logo} alt={t('studio_name')} className="logo-image" />
        </div>
        
        <p className="welcome-text">{t('welcome_to_studio')}</p>
        
        <h2>{t('create_account')}</h2>
        
        {tokenError}
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {success ? (
          <div className="success-message">
            <p>{t('account_created_success')}</p>
            <p>{t('redirecting_to_login_with_email', { email })}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">{t('password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('create_password')}
                required
                minLength="6"
                disabled={!token || loading}
              />
              <small>{t('password_min_length_hint')}</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('confirm_password')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('confirm_your_password')}
                required
                disabled={!token || loading}
              />
            </div>
            
            <button 
              type="submit" 
              className="auth-button"
              disabled={!token || loading}
            >
              {loading ? t('creating_account') : t('create_account')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClaimAccount; 